import { AuthContext } from 'Contexts/AuthContext';
import React, { useState, useEffect, useContext } from 'react';
import { Guest } from 'types/reservations';
import { searchGuests, searchGuestsMeilisearch } from 'api/GuestBook';
import useGuestbookVersion from './useGuestbookVersion';
import { useDebounce } from 'utils/helper';

type Props = {
  search: null | string;
  page?: number;
  perPage?: number;
  /** Restaurant country code (e.g. "de", "at", "ch") for phone normalisation */
  country?: string;
};

const useGuestAutoComplete = ({
  search = '',
  page = 0,
  perPage = 10,
  country = 'de',
}: Props) => {
  const { restaurantId, uid } = useContext(AuthContext);
  const { version } = useGuestbookVersion();
  const debouncedSearch = useDebounce(search || '', version === 'v02' ? 150 : 250);

  const [guests, setguests] = useState<Guest[]>([]);
  const [loading, setloading] = useState(true);
  const [error, seterror] = useState<false | string>(false);
  const [hasMore, sethasMore] = useState(false);

  const minChars = version === 'v02' ? 2 : 3;

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < minChars) {
      setguests([]);
      setloading(false);
      seterror(false);
      sethasMore(false);
      return;
    }

    let cancelled = false;
    setloading(true);

    (async () => {
      try {
        let results;

        if (version === 'v02') {
          // Use Meilisearch for v02 (guestbook app) — multi-field fuzzy search
          results = await searchGuestsMeilisearch({
            restaurantId: restaurantId || '',
            search: debouncedSearch,
            page,
            perPage,
            country,
          });
        } else {
          // Use old API for v01 (no guestbook app)
          results = await searchGuests({
            restaurantId: restaurantId || '',
            search: debouncedSearch,
            page,
            perPage,
            sort: 'name',
            uid: uid || undefined,
            version: 'v01',
          });
        }

        if (cancelled) return;

        sethasMore(results.length === perPage);
        setguests(
          results.map((r: any) => ({
            id: r.id,
            email: r.email,
            name: r.name,
            phone: r.phone,
            attr: r.attr,
            lastReservation: r.lastReservation,
            visits: r.visits,
          }))
        );
        setloading(false);
        seterror(false);
      } catch (error: any) {
        if (cancelled) return;

        console.error(error.message);
        seterror(error.message);
        setloading(false);
        sethasMore(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, page, perPage, restaurantId, uid, version, country]);

  return {
    guests,
    loading,
    error,
    hasMore,
  };
};

export default useGuestAutoComplete;
